import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { theme } from 'theme';

import { useGlobalModalContext } from '../../../../../context/GlobalModal';
import { timeStampToDate, timeStampToTime, updatePayout } from '../../../../../services';
import { Button, Input, Modal, Option, SelectForm, Splash } from '../../../../../shared/Components';
import { FlexColumn, FlexContainer, Image, Text } from '../../../../../shared/Typhography';
import { strings } from '../../../../../strings';

interface SendPayoutData {
  id: string;
  paymentMethodEmail?: string | undefined;
  paymentMethod?: string | undefined;
  approvedAmount: number | undefined;
  answer?: string;
  status?: 'approved' | 'completed' | 'requested' | 'pending' | 'rejected';
}

const Row = styled.div`
  display: flex;
  width: 100%;
`;

const DetailsColumn = styled.div`
  padding: 16px;
  flex: 1;
  box-sizing: border-box;
`;

const HistoryColumn = styled.div`
  padding: 16px;
  width: 400px;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const EventBold = styled.span`
  font-weight: 600;
`;

const EventRow = styled.div`
  display: flex;
  flex-flow: row wrap;
`;

const EventItem = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin: 5px;
`;

const EventComment = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 5px;
  border: solid lightgrey 1px;
  border-radius: 4px;
  margin: 5px 0;
`;

const StatusBadge = styled.span`
  color: white;
  padding: 5px;
  border-radius: 4px;
  width: max-content;

  &.requested {
    background: orange;
  }
  &.pending {
    background: #d5d55f;
  }
  &.approved {
    background: #30af30;
  }
  &.rejected {
    background: red;
  }
  &.completed {
    background: #30af30;
  }
`;

interface PayoutDetailsModalProps {
  payout: any;
  refetch?(id: string): void;
  onClose?(): void;
}

interface FormInputs {
  paymentMethodEmail: string;
  paymentMethod: Option<string, string>;
  ApprovedAmount: number;
  Status: any;
  Answer: string;
}

interface EventBoxProps {
  item: any;
  children: any;
  action?: string;
}

const STATUSES = [
  { value: 'requested', label: 'Requested' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'completed', label: 'Completed' },
];

const PAYMENT_METHODS = [{ value: 'paypal', label: 'PayPal' }];

const EventBox = ({ item, children }: EventBoxProps) => {
  return (
    <EventComment>
      <FlexContainer
        flexDirection="row"
        flexWrap="wrap"
        width="100%"
        alignItems="flex-end"
        margin="0 0 10px 0"
      >
        <Image
          src={item.actor?.profileImageUrl}
          borderRadiusType="circle"
          width={'32px'}
          height={'32px'}
          mobWidth={'32px'}
          mobHeight={'32px'}
          alt="Picture"
          margin={{ right: '10px' }}
        />
        <Text>{item?.actor?.Name ?? item?.actor?.name}</Text>
      </FlexContainer>
      {children}
      <FlexContainer flexDirection="row" flexWrap="wrap" width="100%" justifyContent="flex-end">
        <Text size={0.8} margin="">
          {timeStampToDate(item?.createdAt)} {timeStampToTime(item?.createdAt)}
        </Text>
      </FlexContainer>
    </EventComment>
  );
};

const EVENT_RENDER_PIPELINE = [
  (item: any) => {
    if (!item?.status) return <></>;

    return (
      <EventBox item={item}>
        <EventItem>
          {item?.status?.previousValue && (
            <>
              <StatusBadge className={item?.status?.previousValue}>
                {item?.status?.previousValue}
              </StatusBadge>{' '}
              {' -> '}
            </>
          )}
          <StatusBadge className={item?.status?.value}>{item?.status?.value}</StatusBadge>
        </EventItem>
      </EventBox>
    );
  },
  (item: any) => {
    if (!item?.payoutMethod) return <></>;

    return (
      <EventBox item={item}>
        {item?.payoutMethod?.previousValue &&
          item?.payoutMethod?.value &&
          item?.payoutMethod?.value?.length > 0 && (
            <>
              Payout Method changed from {item?.payoutMethod?.previousValue} to{' '}
              {item?.payoutMethod?.value}
            </>
          )}
        {item?.payoutMethod?.previousValue &&
          item?.payoutMethod?.previousValue?.length != 0 &&
          !item?.payoutMethod?.value && <>Payout Method removed</>}
        {(!item?.payoutMethod?.previousValue || item?.payoutMethod?.previousValue?.length == 0) && (
          <>Payout Method set as {item?.payoutMethod?.value}</>
        )}
      </EventBox>
    );
  },

  (item: any) => {
    if (!item?.approvedAmount) return <></>;

    return (
      <EventBox item={item}>
        <Text>
          {item?.approvedAmount?.previousValue && item?.approvedAmount?.value && (
            <>
              Approved Amount from {item?.approvedAmount?.previousValue}$ to{' '}
              {item?.approvedAmount?.value}$
            </>
          )}

          {!item?.approvedAmount?.previousValue && item?.approvedAmount?.value && (
            <>Approved Amount set as {item?.approvedAmount?.value}$</>
          )}

          {item?.approvedAmount?.previousValue && !item?.approvedAmount?.value && (
            <>Approved Amount removed</>
          )}
        </Text>
      </EventBox>
    );
  },
  (item: any) => {
    if (!item?.answer || item?.answer?.value?.length == 0) return <></>;

    return (
      <EventBox item={item}>
        <Text>{item?.answer?.value}</Text>
      </EventBox>
    );
  },
];

export default function PayoutDetailsModal({ payout, refetch, onClose }: PayoutDetailsModalProps) {
  const { hideModal } = useGlobalModalContext();

  const { handleSubmit, control } = useForm<FormInputs>();
  const [isLoading, setLoading] = useState<boolean>(false);

  const onSubmit = handleSubmit(async (data: FormInputs) => {
    if (data?.Status?.value == 'approved' && !data?.ApprovedAmount) {
      toast.error('Payout can not be approved without entering approved amount');
      return;
    }
    if (data?.Status.value == 'completed' && payout.status != 'approved') {
      toast.error('Payout can not be marked as completed if it is not approved before');
      return;
    }

    const sendData: SendPayoutData = {
      id: payout?.uid,
      paymentMethodEmail: data?.paymentMethodEmail ?? undefined,
      paymentMethod: data?.paymentMethod?.value ?? PAYMENT_METHODS[0].value,
      approvedAmount: `${data?.ApprovedAmount}`?.length == 0 ? undefined : +data?.ApprovedAmount,
      status: data?.Status?.value,
      answer: data?.Answer,
    };

    if (sendData.paymentMethodEmail === payout?.paymentMethodEmail)
      delete sendData.paymentMethodEmail;
    if (sendData.approvedAmount === payout?.approvedAmount) delete sendData.approvedAmount;
    if (payout.status === sendData?.status) delete sendData.status;
    if (!sendData.answer || sendData.answer.length == 0) delete sendData.answer;

    try {
      setLoading(true);
      await updatePayout(sendData);

      if (refetch) await refetch(payout?.uid);

      toast.success('Payout successefully updated!');
      if (onClose) onClose();
      hideModal();
    } catch (err) {
      err;
      toast.error(strings.PayoutFailedToUpdated);
    }
    setLoading(false);
  });

  return (
    <Modal
      onRequestClose={() => {
        if (onClose) onClose();
        hideModal();
      }}
      width={1024}
      padding="40px 20px"
    >
      <Row>
        <DetailsColumn>
          <FlexContainer flexWrap="wrap">
            <Image
              cursor="pointer"
              src={payout.artist.profileImageUrl}
              borderRadiusType="circle"
              size="insightsThumbnail"
              alt="Picture"
              margin={{ right: '20px' }}
            />
            <FlexColumn alignItems="flex-start">
              <Text size={2.5} margin="0 0 5px 0">
                {payout.artist.Name}
              </Text>
              <FlexContainer justifyContent="space-between" margin="0 0 20px 0">
                <Text size={1} color="#7B828A">
                  {payout.artist.username ? `@${payout.artist.username}` : ''}
                </Text>
              </FlexContainer>
              <Text size={1.8} weight="bold">
                Requested $
                {payout.requestedAmount % 1 == 0
                  ? `${payout.requestedAmount}.00`
                  : payout.requestedAmount.toFixed(2)}
              </Text>
            </FlexColumn>
          </FlexContainer>
          <form
            style={{ marginTop: '50px' }}
            onSubmit={onSubmit}
            onKeyPress={(event) => {
              if (event.key == 'Enter') event.preventDefault();
            }}
          >
            <Input
              required
              label={strings.PaymentMethodEmail}
              name="paymentMethodEmail"
              type="text"
              control={control}
              width="100%"
              marginBottom={20}
              marginRight={30}
              margin="0 auto 40px"
              defaultValue={payout?.paymentMethodEmail}
            />
            <SelectForm
              required
              label={strings.PaymentMethod}
              placeholder={strings?.PaymentMethod}
              name="paymentMethod"
              control={control}
              values={PAYMENT_METHODS}
              width="100%"
              margin="0 auto 40px"
              defaultValue={
                PAYMENT_METHODS.find((item) => item?.value == payout?.paymentMethod) ??
                PAYMENT_METHODS[0]
              }
            />
            <Input
              required
              label={strings.ApprovedAmount}
              placeholder={strings.EnterApprovedAmount}
              name={'ApprovedAmount'}
              type="number"
              control={control}
              width="100%"
              marginBottom={20}
              marginRight={30}
              margin="0 auto 40px"
              defaultValue={payout?.approvedAmount}
            />
            <SelectForm
              required
              label={strings.Status}
              placeholder={strings?.SelectStatus}
              name={'Status'}
              control={control}
              values={STATUSES}
              width="100%"
              margin="0 auto 40px"
              defaultValue={STATUSES.find((item) => item?.value == payout?.status)}
            />
            <Input
              required
              label="Comment"
              placeholder={strings.EnterYourComment}
              name={'Answer'}
              type="textarea"
              control={control}
              width="100%"
              height="200px"
              marginBottom={20}
              marginRight={30}
              margin="0 auto 40px"
            />
            <FlexContainer justifyContent="center">
              <Button width={300} height={58} type="submit" disabled={isLoading}>
                {isLoading ? (
                  <Splash color={theme.colors.white} width={30} height={30} />
                ) : (
                  strings.submit
                )}
              </Button>
            </FlexContainer>
          </form>
        </DetailsColumn>
        <HistoryColumn>
          <EventItem>
            <EventRow>
              Requested by &nbsp;
              <EventBold>
                {payout?.requestedBy != payout?.artistId
                  ? payout?.requester?.Name ?? payout?.requester?.Name
                  : payout?.artist?.Name}
              </EventBold>
            </EventRow>
            <EventRow>
              <Text margin="0 0 0 auto" size={0.8}>
                {`${timeStampToTime(payout?.createdAt)} ${timeStampToDate(payout?.createdAt)}`}
              </Text>
            </EventRow>
          </EventItem>
          {payout?.history &&
            payout?.history?.map((item: any) => {
              return <>{EVENT_RENDER_PIPELINE.map((render) => render(item))}</>;
            })}
        </HistoryColumn>
      </Row>
    </Modal>
  );
}
